import React, { useState, useContext, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Grid,
  Autocomplete,
  TextField,
  Snackbar,
  Alert,
  Pagination,
  IconButton,
  Box,
} from "@mui/material";
import { CheckBox, CheckBoxOutlineBlank } from "@mui/icons-material";
import axios from "axios";
import { OrderContext } from "../../contexts/OrderContext";

const SearchStock = ({ open, onClose, filter }) => {
  let IMAGE_BASE_URL = "";
  const { storeData } = useContext(OrderContext);

  const {
    itemtypes = [],
    designs = [],
    screwTypes = [],
    finishtypes = [],
    storeInfo = [],
  } = storeData;
  const selectedManufacturer = JSON.parse(localStorage.getItem("selectedMfr"));
  const mfrId = selectedManufacturer?.id;

  if (Array.isArray(storeInfo) && storeInfo.length > 0) {
    const { mail_id, url_prefix } = storeInfo[0];
    IMAGE_BASE_URL = `https://kumuduorderapp.blob.core.windows.net/${url_prefix}/`;
  } else {
    console.log("No store information found.");
  }

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

  const [filters, setFilters] = useState({
    itemType: "",
    design: "",
    grossWtMin: "",
    grossWtMax: "",
    netWtMin: "",
    netWtMax: "",
    finishtype_id: "",
    screw_type_id: "",
    from_date: "",
    to_date: "",
    expiryDate: "",
    imageFilter: "all",
    store_id: mfrId,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(100);
  const [totalCount, setTotalCount] = useState(0);
  const [stockData, setStockData] = useState([]);
  const [selectedTagNos, setSelectedTagNos] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [imageFilter, setImageFilter] = useState("all");
  const [posibleTagNos, setPosibleTagNos] = useState(null);
  useEffect(() => {
    let savedTagNos = [];
    try {
      savedTagNos =
        filter.selectedStock && typeof filter.selectedStock === "string"
          ? JSON.parse(filter.selectedStock)
          : filter.selectedStock || [];
    } catch (error) {
      console.error("Error parsing selectedStock:", error);
    }
    setSelectedTagNos(new Set(savedTagNos));
  }, [filter]);

  const handleChange = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleDropdownChange = (name, newValue) => {
    setFilters((prev) => ({ ...prev, [name]: newValue ? newValue.id : "" }));
  };

  const handleImageFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, imageFilter: e.target.value }));
  };

  const handleSearch = async (page = 1) => {
    setLoading(true);
    setSearched(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/fetchorder/getStockByFilters`,
        {
          ...filters,
          page,
          pageSize,
        }
      );

      const {
        data: fetchedStockData,
        totalCount,
        selectedTagnos,
      } = response.data;

      setStockData(fetchedStockData);
      setTotalCount(totalCount);
      setCurrentPage(page);
      setPosibleTagNos(selectedTagnos);

      const currentPageTagNos = fetchedStockData.map((item) => item.tagno);
      const allSelected = currentPageTagNos.every((tag) =>
        selectedTagNos.has(tag)
      );
      setSelectAll(allSelected);
    } catch (error) {
      console.error("Error fetching stock data:", error);
      setStockData([]);
    }
    setLoading(false);
  };

  const handleSelectAllAcrossAllPages = () => {
    const allSelected = posibleTagNos.every((tag) => selectedTagNos.has(tag));
    if (allSelected) {
      // Remove all
      setSelectedTagNos(new Set());
    } else {
      // Select all
      setSelectedTagNos(new Set(posibleTagNos));
    }
  };

  const handleRowSelection = (tagno) => {
    setSelectedTagNos((prevSelected) => {
      const newSelected = new Set(prevSelected);
      newSelected.has(tagno)
        ? newSelected.delete(tagno)
        : newSelected.add(tagno);
      return newSelected;
    });
  };

  const handleSelectAll = () => {
    const currentPageTagNos = stockData.map((stock) => stock.tagno);
    setSelectedTagNos((prevSelected) => {
      const newSelected = new Set(prevSelected);
      if (selectAll) {
        currentPageTagNos.forEach((tagno) => newSelected.delete(tagno));
      } else {
        currentPageTagNos.forEach((tagno) => newSelected.add(tagno));
      }
      return newSelected;
    });
    setSelectAll(!selectAll);
  };

  useEffect(() => {
    const currentPageTagNos = stockData.map((item) => item.tagno);
    const allSelected = currentPageTagNos.every((tag) =>
      selectedTagNos.has(tag)
    );
    setSelectAll(allSelected);
  }, [selectedTagNos, stockData]);

  const handleSaveCatalogue = async () => {
    if (selectedTagNos.size === 0) {
      showSnackbar("No stocks selected!", "error");
      return;
    }

    const filterData = {
      filterId: filter.filterId,
      name: filter.name,
      itemType: filters.itemType,
      design: filters.design,
      grossWtMin: filters.grossWtMin || null,
      grossWtMax: filters.grossWtMax || null,
      netWtMin: filters.netWtMin || null,
      netWtMax: filters.netWtMax || null,
      selectedStock: JSON.stringify(Array.from(selectedTagNos)),
      expiryDate: filters.expiryDate
        ? new Date(filters.expiryDate)
          .toISOString()
          .slice(0, 19)
          .replace("T", " ")
        : filter.expiryDate
          ? new Date(filter.expiryDate)
            .toISOString()
            .slice(0, 19)
            .replace("T", " ")
          : null,
    };

    const filterDataWithStore = {
      ...filterData,
      store_id: mfrId,
    };

    try {
      await axios.put(
        `${API_BASE_URL}/api/fetchorder/updateFilterTags/${filter.filterId}`,
        filterDataWithStore
      );
      showSnackbar("Catalogue updated successfully!", "success");
      onClose();
    } catch (error) {
      console.error("Error updating catalogue:", error);
      showSnackbar("Failed to update catalogue.", "error");
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const filteredStockData = stockData.filter((item) => {
    if (imageFilter === "all") return true;
    if (imageFilter === "with" && item.imageUrl) return true;
    if (imageFilter === "without" && !item.imageUrl) return true;
    return false;
  });

  return (
    <Dialog open={open} onClose={onClose} fullScreen fullWidth maxWidth="xl">
      <DialogTitle>Search Stock for Filter: {filter.name}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} mt={1}>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              disablePortal
              options={itemtypes}
              getOptionLabel={(option) => option.name}
              value={
                itemtypes.find((item) => item.id === filters.itemType) || null
              }
              onChange={(event, newValue) =>
                handleDropdownChange("itemType", newValue)
              }
              renderInput={(params) => (
                <TextField {...params} label="Item Type" fullWidth />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              disablePortal
              options={designs}
              getOptionLabel={(option) => option.name}
              value={
                designs.find((design) => design.id === filters.design) || null
              }
              onChange={(event, newValue) =>
                handleDropdownChange("design", newValue)
              }
              renderInput={(params) => (
                <TextField {...params} label="Design" fullWidth />
              )}
            />
          </Grid>

          <Grid item xs={6} sm={3}>
            <TextField
              label="Gross Wt Min"
              type="number"
              name="grossWtMin"
              value={filters.grossWtMin}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Gross Wt Max"
              type="number"
              name="grossWtMax"
              value={filters.grossWtMax}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Net Wt Min"
              type="number"
              name="netWtMin"
              value={filters.netWtMin}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Net Wt Max"
              type="number"
              name="netWtMax"
              value={filters.netWtMax}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="From Date"
              type="date"
              name="from_date"
              value={filters.from_date}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="To Date"
              type="date"
              name="to_date"
              value={filters.to_date}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            my: 2,
            display: "flex",
            flexDirection: {
              xs: "column",
              sm: "row",
            },
            alignItems: {
              xs: "stretch",
              sm: "center",
            },
            gap: 2,
          }}
        >
          <Button
            onClick={handleSelectAllAcrossAllPages}
            sx={{
              height: 60,
              minWidth: { sm: 270 },
              width: { xs: "100%", sm: "auto" },
              px: 2,
            }}
            variant="outlined"
            disabled={
              !Array.isArray(posibleTagNos) || posibleTagNos.length === 0
            }
          >
            {Array.isArray(posibleTagNos) &&
              posibleTagNos.length > 0 &&
              posibleTagNos.every((tag) => selectedTagNos.has(tag))
              ? "Remove all"
              : "Select all across all pages"}
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={() => handleSearch(1)}
            disabled={loading}
            sx={{
              flexGrow: { sm: 1 },
              width: { xs: "100%", sm: "auto" },
              height: 60,
            }}
          >
            {loading ? <CircularProgress size={24} /> : "Search"}
          </Button>
        </Box>

        {searched &&
          (stockData.length > 0 ? (
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <IconButton onClick={handleSelectAll}>
                        {selectAll ? (
                          <CheckBox color="primary" />
                        ) : (
                          <CheckBoxOutlineBlank />
                        )}
                      </IconButton>
                      Select all on current page
                    </TableCell>
                    <TableCell>Tag No</TableCell>
                    <TableCell>Item Type</TableCell>
                    <TableCell>Design</TableCell>
                    <TableCell>Metal Type</TableCell>
                    <TableCell>Purity</TableCell>
                    <TableCell>Gross Weight</TableCell>
                    <TableCell>Image</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stockData.map((stock, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <IconButton
                          onClick={() => handleRowSelection(stock.tagno)}
                        >
                          {selectedTagNos.has(stock.tagno) ? (
                            <CheckBox color="primary" />
                          ) : (
                            <CheckBoxOutlineBlank />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>{stock.tagno_alpha || stock.tagno}</TableCell>
                      <TableCell>{stock.itemtype_name}</TableCell>
                      <TableCell>{stock.design_name}</TableCell>
                      <TableCell>{stock.purity_name}</TableCell>
                      <TableCell>{stock.purity}</TableCell>
                      <TableCell>{stock.gross}</TableCell>
                      <TableCell>
                        <img
                          src={`${IMAGE_BASE_URL}${stock.tagno_alpha || stock.tagno
                            }.jpg`}
                          alt="Stock Item"
                          width="50"
                          height="50"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography mt={2}>
              No stock found with the applied filters.
            </Typography>
          ))}

        {totalCount > 0 && (
          <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
            <Pagination
              count={Math.ceil(totalCount / pageSize)}
              page={currentPage}
              onChange={(event, value) => handleSearch(value)}
              color="primary"
            />
          </Grid>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSaveCatalogue}
          variant="contained"
          color="primary"
        >
          Save Catalogue
        </Button>
      </DialogActions>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        sx={{
          '&.MuiSnackbar-root': {
            bottom: '100px !important', // force override snackbar position
          }
        }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default SearchStock;
